import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { storyblokEditable } from '@storyblok/react';
import { object } from 'prop-types';
import { memo } from 'react';
import { useIntl } from 'react-intl';
import {
  MARK_LINK,
  NODE_HEADING,
  NODE_HR,
  NODE_IMAGE,
  NODE_PARAGRAPH,
  render,
} from 'storyblok-rich-text-react-renderer';
import { openCookieSettings } from '../../../util/consent/consentUI';
import Link from '../../Links/Link/Link';
import { storyblokLinkToRoute } from '../util/storyblokLinkToRoute';
import { withBaseComponent } from '../hoc/withBaseComponent';
import StoryblokButton from './StoryblokButton';
import StoryblokChip from './StoryblokChip';
import StoryblokImage from './StoryblokImage';

// ========== Styles ==========
const richTextBoxStyle = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  whiteSpace: 'break-spaces',
  wordBreak: 'break-word',
  ul: {
    listStyle: 'disc',
    padding: 'revert',
    'li > p': {
      margin: 0,
    },
  },
};

const headingStyle = {
  margin: '0 0 1.6rem',
};

const paragraphStyle = {
  margin: '0 0 1rem',
};

const blockContainerStyle = {
  display: 'flex',
  flexDirection: 'column',
};

// Anchors that reopen the cookie-consent settings dialog instead of scrolling —
// lets the privacy policy link straight to the consent/withdrawal controls.
const COOKIE_SETTINGS_ANCHORS = ['cookie-einstellungen', 'cookie-settings'];

// Smooth-scroll to an in-page anchor without a full navigation.
const handleAnchorClick = (event, href) => {
  event.preventDefault();
  const anchorId = href.split('#')[1];
  if (anchorId && COOKIE_SETTINGS_ANCHORS.includes(anchorId.toLowerCase())) {
    openCookieSettings();
    return;
  }
  const targetElement = anchorId ? document.getElementById(anchorId) : null;
  if (targetElement) {
    targetElement.scrollIntoView({ behavior: 'smooth' });
  }
};

/**
 * @param {object} props
 * @param {object} props.blok
 * @param {object} props.baseStyles
 */
const StoryblokRichTextComponent = ({ blok, baseStyles }) => {
  const intl = useIntl();
  const { general, extra = { id: '' } } = blok;
  const { id } = extra;
  const { content } = general;

  // Single source of truth for every inline link in the rich text, regardless
  // of the node it lives in (paragraph, heading, list, quote, …). Storyblok's
  // `linktype` tells us how to resolve the destination:
  //   - story  → internal page → locale-prefixed app route (client-side nav)
  //   - email  → mailto: (native <a>)
  //   - tel / mailto in href → native <a> (the custom Link prefixes non-http
  //                            strings with "/", which would corrupt them)
  //   - url    → external http(s) → keep the raw href via the custom Link
  // Running an external URL through storyblokLinkToRoute is exactly what
  // produced the bogus /<locale>/https%3A%2F%2F… links.
  const renderLink = (children, attrs = {}, key) => {
    const { linktype, href = '', target } = attrs;

    // In-page anchor (#section) — smooth scroll, no navigation.
    if (href.startsWith('#')) {
      return (
        <a href={href} key={key} onClick={(event) => handleAnchorClick(event, href)}>
          {children}
        </a>
      );
    }

    const normalizedHref =
      linktype === 'email' && !href.startsWith('mailto:') ? `mailto:${href}` : href;

    if (normalizedHref.startsWith('mailto:') || normalizedHref.startsWith('tel:')) {
      return (
        <a href={normalizedHref} key={key} target={target}>
          {children}
        </a>
      );
    }

    const to = linktype === 'story' ? storyblokLinkToRoute(href, intl.locale) : href;

    return (
      <Link key={key} target={target} to={to}>
        {children}
      </Link>
    );
  };

  return (
    <Box
      id={id}
      {...storyblokEditable(blok)}
      sx={{
        ...richTextBoxStyle,
        ...baseStyles,
      }}
    >
      {render(content, {
        markResolvers: {
          // Links are resolved here so they render consistently in every node
          // type (paragraph, heading, list item, quote, …).
          [MARK_LINK]: (children, attrs) => renderLink(children, attrs),
        },
        nodeResolvers: {
          [NODE_HEADING]: (children, { level }) => (
            <Typography sx={headingStyle} variant={`h${level}`}>
              {children}
            </Typography>
          ),
          // [NODE_CODEBLOCK]: (children, {}) => <></>, // has class
          [NODE_IMAGE]: (children, { src, alt, title }) => (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <img alt={alt} height="auto" src={src} title={title} width="100%" />
            </Box>
          ),

          // [NODE_EMOJI]: (children, { name, emoji, fallbackImage }) => <></>,
          // Links are handled by the MARK_LINK resolver above, so the paragraph
          // only has to apply its typography/styling.
          [NODE_PARAGRAPH]: (children) => (
            <Typography sx={paragraphStyle} variant="body1">
              {children}
            </Typography>
          ),

          // [NODE_QUOTE]: (children) => <></>,
          // [NODE_OL]: (children) => <></>,
          // [NODE_UL]: (children) => <></>,
          // [NODE_LI]: (children) => <></>,
          [NODE_HR]: () => <Divider sx={{ my: 2 }} />,
          // [NODE_BR]: () => <></>,
        },
        blokResolvers: {
          button: (blok) => (
            <Box sx={{ ...blockContainerStyle, margin: '0.75rem 0' }}>
              <StoryblokButton blok={blok} />
            </Box>
          ),
          image: (blok) => (
            <Box sx={blockContainerStyle}>
              <StoryblokImage blok={blok} />
            </Box>
          ),
          chip: (blok) => (
            <Box sx={blockContainerStyle}>
              <StoryblokChip blok={blok} />
            </Box>
          ),
        },
      })}
    </Box>
  );
};

StoryblokRichTextComponent.displayName = 'StoryblokRichText';
const StoryblokRichText = withBaseComponent(StoryblokRichTextComponent);

export default memo(StoryblokRichText);
